/**
 * Bulk Download Service
 * Handles downloading multiple files as a zip archive
 */

import JSZip from 'jszip';
import { saveAs } from 'file-saver';

export interface FileToDownload {
  name: string;
  url: string;
  folder?: string;
}

/**
 * Download multiple files as a zip archive
 * @param files Array of files to download
 * @param zipName Name for the zip file
 */
export async function downloadFilesAsZip(
  files: FileToDownload[],
  zipName: string = 'myportal-files.zip'
): Promise<void> {
  if (files.length === 0) {
    throw new Error('No files selected for download');
  }

  const zip = new JSZip();
  
  // Show progress (you can use this with a progress bar)
  let completed = 0;
  const total = files.length;

  try {
    // Fetch all files and add to zip
    const downloadPromises = files.map(async (file) => {
      try {
        // Fetch the file from the SAS URL
        const response = await fetch(file.url);
        
        if (!response.ok) {
          throw new Error(`Failed to download ${file.name}`);
        }

        const blob = await response.blob();
        
        // Organize files in zip with folder structure if provided
        const zipPath = file.folder ? `${file.folder}/${file.name}` : file.name;
        zip.file(zipPath, blob);
        
        completed++;
        console.log(`Downloaded ${completed}/${total}: ${file.name}`);
        
      } catch (error) {
        console.error(`Error downloading ${file.name}:`, error);
        // Continue with other files even if one fails
      }
    });

    // Wait for all downloads to complete
    await Promise.all(downloadPromises);

    // Generate the zip file
    console.log('Generating zip file...');
    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6 // Balance between speed and compression
      }
    });

    // Trigger download
    saveAs(zipBlob, zipName);
    console.log('Zip download started');
    
  } catch (error) {
    console.error('Error creating zip file:', error);
    throw error;
  }
}

/**
 * Download multiple files as a zip with progress callback
 * @param files Array of files to download
 * @param zipName Name for the zip file
 * @param onProgress Callback function for progress updates
 */
export async function downloadFilesAsZipWithProgress(
  files: FileToDownload[],
  zipName: string = 'myportal-files.zip',
  onProgress?: (current: number, total: number, fileName: string) => void
): Promise<void> {
  if (files.length === 0) {
    throw new Error('No files selected for download');
  }

  const zip = new JSZip();
  let completed = 0;
  const total = files.length;

  try {
    // Fetch all files and add to zip
    const downloadPromises = files.map(async (file) => {
      try {
        const response = await fetch(file.url);
        
        if (!response.ok) {
          throw new Error(`Failed to download ${file.name}`);
        }

        const blob = await response.blob();
        const zipPath = file.folder ? `${file.folder}/${file.name}` : file.name;
        zip.file(zipPath, blob);
        
        completed++;
        if (onProgress) {
          onProgress(completed, total, file.name);
        }
        
      } catch (error) {
        console.error(`Error downloading ${file.name}:`, error);
      }
    });

    await Promise.all(downloadPromises);

    // Generate the zip file
    const zipBlob = await zip.generateAsync({ 
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: {
        level: 6
      }
    });

    // Trigger download
    saveAs(zipBlob, zipName);
    
  } catch (error) {
    console.error('Error creating zip file:', error);
    throw error;
  }
}

/**
 * Check if browser supports the required APIs
 */
export function isBulkDownloadSupported(): boolean {
  return typeof Blob !== 'undefined' && typeof URL !== 'undefined';
}
